import { faQuestionCircle } from '@fortawesome/free-regular-svg-icons';
import React, { useContext, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { ConditionalRendering } from '@components/common/conditional-rendering';
import { CurrencyInput, ValidationErrorMessage } from '@components/common/form';
import { INPUT_TYPES } from '@components/common/form/constants/fieldTypes';
import Tooltip from '@components/common/tooltip/Tooltip';
import { usePermissions } from '@hooks/usePermissions';
import useValidateSubsidyAmount from '../../../../../hooks/useValidateSubsidyAmount';
import { currencyChange } from '../../common/enrollmentUtils';
import { IEnrollmentPremiumDto } from '../../common/types';
import { EnrollmentContext } from '../../context/MemberMyEnrollmentProvider';
import { StyledField } from '../../styles/EnrollmentSharedStyled';

const ColumnStateSubsidy2Amount = ({ premium }: { premium: IEnrollmentPremiumDto }) => {
    const permissions = useSelector((state: any) => state.memberEnrollment?.permissions);
    const { userPermissions } = permissions || {};
    const { viewEditableFields } = usePermissions();
    const validateSubsidy = useValidateSubsidyAmount();
    const { enrollmentPremiumFormContext, selectedEnrollment } = useContext(EnrollmentContext);
    const {
        watch,
        setValue,
        trigger,
        formState: { errors },
    } = enrollmentPremiumFormContext;
    const values = watch();
    const { isEnrollmentPendingOrPendCancelled } = usePermissions();

    const onStateSubsidyChange = e => {
        validateSubsidy({ ...premium, stateSubsidy2Amount: Number(parseFloat(e.target.value).toFixed(2)) });
        currencyChange(setValue, 'stateSubsidy2Amount', premium, e.target.value);
        trigger([`enrollmentPremiumDtoList.${premium.month - 1}`]);
    };

    const isDisabled = useMemo(
        () =>
            !premium.isFinancial ||
            (!selectedEnrollment.isStateSubsidy2Enabled && premium.stateSubsidyAmount <= 0) ||
            !userPermissions?.memberEnrlmntAptcEditable ||
            isEnrollmentPendingOrPendCancelled(selectedEnrollment),
        [premium.isFinancial],
    );

    return (
        <ConditionalRendering
            isTrue={selectedEnrollment.isStateSubsidy2Enabled && viewEditableFields(userPermissions, values, premium)}
        >
            <StyledField>
                <CurrencyInput
                    // @ts-ignore
                    label={{
                        wrapperClasses: [''],
                        labelClasses: [''],
                        labelText: '',
                        showRequired: false,
                        isInputRequired: false,
                    }}
                    input={{
                        id: 'stateSubsidy2Amount',
                        isInputRequired: true,
                        type: INPUT_TYPES.TEXT,
                        value: premium.stateSubsidy2Amount,
                        disabled: isDisabled,
                        dataTestId: 'stateSubsidy2Amount',
                        name: 'stateSubsidy2Amount',
                        placeholder: '',
                    }}
                    actions={{
                        handleChange: e => onStateSubsidyChange(e),
                    }}
                    maskOptions={{
                        allowDecimal: true,
                        prefix: '',
                        decimalLimit: '2',
                        integerLimit: 10,
                        includeThousandsSeparator: false,
                        allowLeadingZeroes: true,
                    }}
                    showIcon={true}
                />

                <ConditionalRendering isTrue={!premium.isFinancial}>
                    <Tooltip
                        index={`stateSubsidy2AmountTooltip-${premium.month}`}
                        id="questinMark"
                        elementId="stateSubsidy2Amount"
                        content="member.enrollment_list.premiumhistory.tooltip.stateSubsidy2"
                        place="top"
                        icon={faQuestionCircle}
                        dataAutomationId="stateSubsidy2Amount"
                    />
                </ConditionalRendering>
            </StyledField>
            <ConditionalRendering isTrue={premium.isFinancial}>
                <ValidationErrorMessage
                    isVisible={errors?.enrollmentPremiumDtoList?.[premium.month - 1]?.stateSubsidy2Amount}
                    messageText={errors?.enrollmentPremiumDtoList?.[premium.month - 1]?.stateSubsidy2Amount?.message}
                />
            </ConditionalRendering>
        </ConditionalRendering>
    );
};

export default ColumnStateSubsidy2Amount;
